import { Injectable, inject } from '@angular/core';
import { Auth, authState, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithCustomToken, signOut, User } from '@angular/fire/auth';
import { Database, ref, get } from '@angular/fire/database';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

export interface UserSession {
  uid: string;
  email: string;
  orgCode: string;
  orgName: string;
  displayName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private db: Database = inject(Database);
  private firestore: Firestore = inject(Firestore);
  private functions: Functions = inject(Functions);
  private authState$ = authState(this.auth);

  private userSubject = new BehaviorSubject<UserSession | null>(null);

  constructor() {
    this.authState$.pipe(
      switchMap((firebaseUser: User | null) => {
        console.log('[AuthService] authState - user:', firebaseUser);
        if (!firebaseUser) {
          return of(null);
        }

        return from(this.resolveSession(firebaseUser)).pipe(
          switchMap(session => {
            if (!session) {
              console.warn('[AuthService] Session resolution failed on load/state-change. Signing out.');
              return from(signOut(this.auth)).pipe(map(() => null));
            }
            return of(session);
          })
        );
      })
    ).subscribe((session) => {
      this.userSubject.next(session);
    });
  }

  /**
   * Helper function to fetch details from RTDB and Firestore, validating permissions.
   */
  private async resolveSession(firebaseUser: User): Promise<UserSession | null> {
    try {
      // 1. Fetch user roles and orgCode from Realtime Database (/provider-users/{uid})
      const userRef = ref(this.db, `provider-users/${firebaseUser.uid}`);
      const rtdbSnap = await get(userRef);
      if (!rtdbSnap.exists()) {
        console.warn('[AuthService] No user details found in Realtime Database.');
        return null;
      }

      const userData = rtdbSnap.val();
      console.log('[AuthService] User details from Realtime Database:', userData);

      // Verify userData.roles.adm is true
      if (userData.roles?.adm !== true) {
        console.warn('[AuthService] Unauthorized: User is not an administrator (roles.adm is not true).');
        return null;
      }

      const orgCode = userData.orgCode;
      if (!orgCode) {
        console.warn('[AuthService] Unauthorized: User does not have an associated orgCode.');
        return null;
      }

      // 2. Fetch organization name from Firestore (/provider-orgs/provider-org-{orgCode})
      const orgDocRef = doc(this.firestore, `provider-orgs/provider-org-${orgCode}`);
      const orgSnap = await getDoc(orgDocRef);
      let orgName = 'MetroMED Eval Center'; // Default fallback

      if (orgSnap.exists()) {
        const orgData = orgSnap.data();
        if (orgData) {
          orgName = orgData['name'] || orgName;
        }
        console.log('[AuthService] Organization name resolved from Firestore:', orgName);
      } else {
        console.warn(`[AuthService] No organization document found in Firestore for path: provider-orgs/provider-org-${orgCode}`);
      }

      // 3. Build the UserSession object
      return {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        orgCode: orgCode,
        orgName: orgName,
        displayName: userData.displayName || firebaseUser.displayName || '',
      };
    } catch (error) {
      console.error('[AuthService] Error resolving user session:', error);
      return null;
    }
  }

  getLoggedInUser(): Observable<UserSession | null> {
    return this.userSubject.asObservable();
  }

  getCurrentUser(): UserSession | null {
    return this.userSubject.value;
  }

  getOrgCode(): string | null {
    const user = this.getCurrentUser();
    return user ? user.orgCode : null;
  }

  async login(email: string, password: string): Promise<boolean> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const session = await this.resolveSession(userCredential.user);
      
      if (!session) {
        console.warn('[AuthService] User logged in with Firebase but failed permission validation. Logging out.');
        await signOut(this.auth);
        this.userSubject.next(null);
        return false;
      }
      
      this.userSubject.next(session);
      return true;
    } catch (error) {
      console.error('[AuthService] Login failed:', error);
      return false;
    }
  }

  async resetPassword(email: string): Promise<boolean> {
    try {
      await sendPasswordResetEmail(this.auth, email);
      return true;
    } catch (error) {
      console.error('[AuthService] Password reset failed:', error);
      return false;
    }
  }

  async exchangeHandshakeForToken(handshakeId: string): Promise<string> {
    try {
      const exchangeFn = httpsCallable<{ handshakeId: string }, { customToken: string }>(
        this.functions,
        'exchangeHandshakeForToken'
      );
      const result = await exchangeFn({ handshakeId });
      if (!result.data?.customToken) {
        throw new Error('Cloud Function did not return a customToken.');
      }
      return result.data.customToken;
    } catch (error) {
      console.error('[AuthService] Handshake exchange failed:', error);
      throw error;
    }
  }

  async loginWithCustomToken(customToken: string): Promise<boolean> {
    try {
      const userCredential = await signInWithCustomToken(this.auth, customToken);
      const session = await this.resolveSession(userCredential.user);
      
      if (!session) {
        console.warn('[AuthService] User logged in with Custom Token but failed permission validation. Logging out.');
        await signOut(this.auth);
        this.userSubject.next(null);
        return false;
      }
      
      this.userSubject.next(session);
      return true;
    } catch (error) {
      console.error('[AuthService] Custom token login failed:', error);
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
      this.userSubject.next(null);
    } catch (error) {
      console.error('[AuthService] Logout failed:', error);
      throw error;
    }
  }
}
